import React, { ReactNode, useEffect, useState } from 'react'
import { injectIntl, WrappedComponentProps, FormattedMessage } from 'react-intl'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import getConstants from '../../constants'
import {
  hasSimpleModeFlag,
  getWindowDimensions,
  getLanguageOptions,
  getCountryValue,
  EUCountryLanguageOptions,
} from 'utils'
import {
  OverlayBackdrop,
  LayoutWrapper,
  NavigationHeader,
  ButtonIcon,
  FlyoutSelect,
  Caption,
  Footer,
  Modal,
  LinkImage,
  ContentAlignment,
  Icon,
  Body,
  Tile,
  Spacer,
} from 'shared-global-ec-uikit/components/dist/react/esm'
import { hasOneTrustFooter } from 'helpers/oneTrust'
import { BRAND_LIST, Countries } from 'typings'
import OneTrustFooter from 'components/OneTrustFooter'
import { isGu, isPlst, isUniqlo } from 'helpers/logo'
import { isNativeAppByUserAgent } from 'helpers/nativeApp'
import FooterLinks from 'components/FooterLinks'

// TODO
// move to components/AppTemplate
// move styling into ./styles.css

type FlyoutOption = {
  label: string
  value: string
}

type Props = {
  children?: ReactNode
} & WrappedComponentProps &
  RouteComponentProps

const CONSTANTS = getConstants()

export const AppLayout = ({
  intl,
  children,
  location: { search, pathname },
}: Props) => {
  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false)

  const [isSelectedLang, setSelectedLang] = useState('')
  const [currentCountryLanguage, setCurrentCountryLanguage] = useState('')
  const [languageOptionsMap, setLanguageOptionsMap] = useState<
    {
      key: string
      lid: string
      lang: string
      label: string
    }[]
  >([])
  const isSimpleMode = hasSimpleModeFlag(search)
  const windowWidth = getWindowDimensions().width
  const oneTrustLabel = intl.formatMessage({
    id: 'text_do_not_sell_personal_info_link',
  })

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    setSelectedLang(CONSTANTS.LANGUAGE)
    if (CONSTANTS.SHOW_COUNTRY_LANGUAGE_MODAL[CONSTANTS.BRAND]) {
      const languageOptions: Record<
        string,
        { lid: string; label: string; locale?: string }
      > = CONSTANTS.IS_EU
        ? EUCountryLanguageOptions
        : CONSTANTS.LANGUAGE_OPTIONS
      setCurrentCountryLanguage(`${CONSTANTS.COUNTRY}/${CONSTANTS.LANGUAGE}`)
      const languageOptionsNormalized = Object.entries(languageOptions).map(
        ([key, { lid, label, locale }]) => ({
          key,
          lid,
          lang: intl.formatMessage({
            id: lid,
          }),
          get label() {
            let res = label.replace(
              '#{PLACEHOLDER_COUNTRY}',
              intl.formatMessage({
                id: lid,
              }),
            )
            if (locale) {
              res = res.replace(
                '#{PLACEHOLDER_LOCALE}',
                intl.formatMessage({
                  id: locale,
                }),
              )
            }

            return res
          },
        }),
      )
      setLanguageOptionsMap(languageOptionsNormalized)
    }
  }, [])

  const handleChange = (
    event: React.KeyboardEvent<HTMLLIElement> | React.MouseEvent<HTMLLIElement>,
    value: string,
  ) => {
    window.location.href = `${window.location.origin}/${CONSTANTS.COUNTRY}/${value}/`
  }

  const onToggle = (
    e: KeyboardEvent | React.SyntheticEvent<HTMLElement, Event>,
  ) => {
    e.stopPropagation()
    setVisible(!visible)
  }

  const getModal = () => {
    /* DS class names are being used here to match the UI
     * these class names are used for supporting the hover effect
     * and to position the selected icon.
     * TO DO: remove the class name usage
     * */

    const flyoutListClass = 'fr-ec-flyout-select__list-item'
    const flyoutListActiveClass = 'fr-ec-flyout-select__list-item--active'
    const flyoutListActiveIconClass =
      'fr-ec-flyout-select__list-item-selected-icon'
    return (
      <Modal
        trapFocus={false}
        closeLabel={intl.formatMessage({ id: 'text_close' })}
        closeTitle={intl.formatMessage({ id: 'text_close' })}
        isOpen={visible}
        onRequestClose={onToggle}
        id="footer-select-country-and-language"
        labelElement="h5"
        title={intl.formatMessage(
          CONSTANTS.IS_EU
            ? { id: 'text_select_country_and_language' }
            : { id: 'text_langsettings' },
        )}
        titleId="modal-title"
        variant="large"
      >
        <Spacer element="div" variant="spacing-04">
          {languageOptionsMap.map(item =>
            currentCountryLanguage === item.key ? (
              <ContentAlignment
                className={`${flyoutListClass} ${flyoutListActiveClass}`}
                element="div"
                direction="row"
                justifyContent="space-between"
                alignment="center"
                key={item.key}
              >
                <Body element="p" variant="standard-emphasized">
                  {item.label}
                </Body>
                {currentCountryLanguage === item.key && (
                  <Icon
                    name="check-large"
                    className={flyoutListActiveIconClass}
                  />
                )}
              </ContentAlignment>
            ) : (
              <Tile
                element="a"
                href={
                  getCountryValue(item.key) === CONSTANTS.COUNTRY
                    ? window.location.href.replace(
                        `${CONSTANTS.COUNTRY}/${CONSTANTS.LANGUAGE}`,
                        `${item.key}`,
                      )
                    : `${window.location.origin}/${item.key}/`
                }
                target="_self"
                key={item.key}
                className={flyoutListClass}
              >
                <ContentAlignment
                  element="div"
                  direction="column"
                  alignment="stretch"
                >
                  <Body element="p">{item.label}</Body>
                </ContentAlignment>
              </Tile>
            ),
          )}
        </Spacer>
      </Modal>
    )
  }

  const renderHeaderContent = () => {
    let renderHeader = <></>
    if (CONSTANTS.SHOW_COUNTRY_LANGUAGE_MODAL[CONSTANTS.BRAND]) {
      const getLabelForLanguageIcon = () => {
        //For EU minor countries, do not display the "EU-" prefix in the language icon label
        const countryValue = currentCountryLanguage.replace(/^eu-/, '')
        return countryValue.replace('/', '-')
      }

      renderHeader = (
        <>
          <ButtonIcon
            element="button"
            icon="global"
            label={getLabelForLanguageIcon()}
            countrySelectorLabel
            hideLabel={false}
            onClick={() => setVisible(!visible)}
          />
          {getModal()}
        </>
      )
    } else if (Object.keys(CONSTANTS.LANGUAGES).length > 1) {
      renderHeader = (
        <>
          <ButtonIcon
            element="button"
            icon="global"
            label={intl.formatMessage({
              id: 'text_langsettings',
            })}
            onClick={() => setLanguageMenuOpen(!isLanguageMenuOpen)}
          />
          <FlyoutSelect
            onHideVisibility={function noRefCheck() {}}
            onSelect={handleChange}
            options={
              getLanguageOptions(intl, CONSTANTS.LANGUAGES) as FlyoutOption[]
            }
            value={isSelectedLang}
            visible={isLanguageMenuOpen}
          />
        </>
      )
    }
    return renderHeader
  }
  const renderIcons = () => {
    const GUIcon = (
      <LinkImage element="a" href={CONSTANTS.BASE_URL}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 40 40"
          width="34"
          height="34"
        >
          <title>ジーユートップへ</title>
          <path fill="#0000A4" d="M0 0h40v40H0z"></path>
          <path
            d="M20.23 18.55h-8.6v2.91h5.64a5.83 5.83 0 1 1-1.79-5.82h3.71a8.92 8.92 0 1 0 1 2.91zm13.95-6.19v9.45a4 4 0 0 1-8 0v-9.45h-2.91v9.45a6.91 6.91 0 0 0 13.82 0v-9.45z"
            fill="#FFFF00"
          ></path>
        </svg>
      </LinkImage>
    )

    const UQIcon = (
      <LinkImage element="a" href={CONSTANTS.BASE_URL}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 90 40"
          width="75"
          height="34"
        >
          <title>ユニクロトップへ</title>
          <path fill="red" d="M50 0h40v40H50zM0 0h40v40H0z"></path>
          <g fill="#fff">
            <path d="M79.48 5.47h2.53v12.64h-2.53zM63.47 13.9a4.21 4.21 0 0 1-8.42 0V5.47h2.53v8.43a1.68 1.68 0 1 0 3.36 0V5.47h2.53zm11.79 20.63h-8.42V21.89h2.53V32h5.89v2.53zm0-16.42h-2.53l-3.36-7.22v7.22h-2.53V5.47h2.53l3.36 7.22V5.47h2.53v12.64zm-16 3.78a4.21 4.21 0 0 0-4.21 4.22v4.21a4.21 4.21 0 0 0 4.21 4.21 4.34 4.34 0 0 0 .82-.07l.86 2.6h2.53l-1.25-3.75a4.2 4.2 0 0 0 1.25-3v-4.2a4.21 4.21 0 0 0-4.21-4.22m1.68 8.43a1.68 1.68 0 1 1-3.36 0v-4.21a1.68 1.68 0 1 1 3.36 0zm19.8-8.43a4.22 4.22 0 0 0-4.22 4.22v4.21a4.21 4.21 0 0 0 8.42 0v-4.21a4.21 4.21 0 0 0-4.21-4.22m1.68 8.43a1.68 1.68 0 0 1-3.37 0v-4.21a1.68 1.68 0 0 1 3.37 0zM22.74 15.16H34.1v2.52H22.74zM24 5.47h8.84V8H24zm-9.26 0H7.15V8h5.06v7.16H5.9v2.52h11.36v-2.52h-2.52V5.47zm8 16.84v12.22H34.1V22.31zM31.57 32h-6.31v-7.16h6.31zM7.15 22.31l-1.28 6.12h2.52l.76-3.59h5.07L12.73 32H5.14l-.51 2.53h10.11l2.52-12.22H7.15z"></path>
          </g>
        </svg>
      </LinkImage>
    )

    const PLIcon = (
      <LinkImage element="a" href={CONSTANTS.BASE_URL}>
        <svg
          width="34"
          height="34"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>プラステトップへ</title>
          <g clip-path="url(#clip0_1154_194774)">
            <path d="M0 0V40H40V0H0Z" fill="black"></path>
            <path
              d="M35.4543 12.3631H28.5441V14.5442H30.9086V27.6349H33.0897V14.5442H35.4543V12.3631Z"
              fill="white"
            ></path>
            <path
              d="M15.8182 25.4538V12.3631H13.6351V27.6349H19.4548V25.4538H15.8182Z"
              fill="white"
            ></path>
            <path
              d="M12.1776 15.9855C12.0748 13.625 10.3069 12.3046 9.02486 12.103C8.76885 12.0627 8.41809 12.0284 8.16208 12.0143C7.85568 11.9982 7.63797 12.0002 7.63797 12.0002L5.45685 12.0042V27.637L7.63797 27.6329V22.1821H8.16208C8.42011 22.1821 8.77086 22.1176 9.02486 22.0773C10.3069 21.8737 12.0184 20.636 12.1776 18.1727C12.2018 17.8038 12.1998 17.4631 12.1998 17.0922C12.1998 16.7596 12.1937 16.3443 12.1776 15.9875V15.9855ZM10.0005 17.8864C9.95214 18.6988 9.74249 19.5757 8.65999 19.9022C8.406 19.9647 8.12782 19.999 7.90608 19.999H7.63595V14.1813H7.90608C8.12983 14.1813 8.40801 14.2095 8.65999 14.272C9.74047 14.5986 9.95012 15.4755 10.0005 16.2879C10.0086 16.4068 10.0146 16.814 10.0146 17.0902C10.0146 17.3663 10.0086 17.7675 10.0005 17.8864Z"
              fill="white"
            ></path>
            <path
              d="M23.1941 20.6824C24.6153 22.1962 25.4518 22.8433 25.4518 24.3572C25.4518 25.1232 24.831 25.8167 24.0004 25.8167C23.1699 25.8167 22.545 25.1232 22.545 24.1516V23.9984H20.3639V24.3612C20.3639 26.369 21.9887 27.9978 24.0004 27.9978C26.0122 27.9978 27.637 26.367 27.637 24.3552C27.637 22.7687 26.9617 21.5028 25.474 20.0272L24.7826 19.3156C23.3614 17.8017 22.547 17.1406 22.547 15.6347C22.547 14.8687 23.1699 14.1793 24.0025 14.1793C24.835 14.1793 25.4579 14.8607 25.4579 15.8444V15.9976H27.639V15.6388C27.639 13.629 26.0082 11.9982 23.9984 11.9982C21.9887 11.9982 20.3578 13.629 20.3578 15.6388C20.3578 17.1809 21.0997 18.5033 22.5107 19.9869L23.1981 20.6803L23.1941 20.6824Z"
              fill="white"
            ></path>
          </g>
          <defs>
            <clipPath id="clip0_1154_194774">
              <rect width="40" height="40" fill="white"></rect>
            </clipPath>
          </defs>
        </svg>
      </LinkImage>
    )
    const UQKRIcon = (
      <LinkImage element="a" href={CONSTANTS.BASE_URL}>
        <svg
          width="34"
          height="34"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>UNIQLO 홈</title>
          <path d="M0 0H40V40H0V0Z" fill="#FF0000" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.26006 18.1059C11.5836 18.1059 13.4678 16.2235 13.4701 13.9V5.46997H10.9401V13.9C10.9401 14.8278 10.1879 15.58 9.26006 15.58C8.33226 15.58 7.58006 14.8278 7.58006 13.9V5.46997H5.05006V13.9C5.05236 16.2235 6.93656 18.1059 9.26006 18.1059ZM32.0101 5.46997H29.4801V18.11H32.0101V5.46997ZM25.2601 34.53H16.8401V21.89H19.3701V32H25.2601V34.53ZM22.7301 18.11H25.2601V5.46997H22.7301V12.69L19.3701 5.46997H16.8401V18.11H19.3701V10.89L22.7301 18.11ZM9.26006 21.89C8.14176 21.89 7.06946 22.3349 6.27956 23.1266C5.48976 23.9183 5.04736 24.9917 5.05006 26.11V30.32C5.05006 32.6451 6.93496 34.53 9.26006 34.53C9.53496 34.5327 9.80956 34.5093 10.0801 34.46L10.9401 37.06H13.4701L12.2201 33.31C13.0225 32.5183 13.4729 31.4372 13.4701 30.31V26.11C13.4728 24.9917 13.0304 23.9183 12.2406 23.1266C11.4507 22.3349 10.3784 21.89 9.26006 21.89ZM9.26006 32C10.1879 32 10.9401 31.2478 10.9401 30.32V26.11C10.9401 25.1822 10.1879 24.43 9.26006 24.43C8.33226 24.43 7.58006 25.1822 7.58006 26.11V30.32C7.58006 31.2478 8.33226 32 9.26006 32ZM30.735 21.89C28.4067 21.8927 26.5201 23.781 26.5201 26.11V30.32C26.5201 32.6451 28.405 34.53 30.7301 34.53C33.0552 34.53 34.9401 32.6451 34.9401 30.32V26.11C34.9427 24.9917 34.5004 23.9183 33.7106 23.1266C32.9218 22.336 31.8515 21.8913 30.735 21.89ZM30.735 21.89L30.7301 21.89H30.7401L30.735 21.89ZM30.7251 32.005C31.6557 32.005 32.4101 31.2506 32.4101 30.32V26.11C32.4101 25.1794 31.6557 24.425 30.7251 24.425C29.7945 24.425 29.0401 25.1794 29.0401 26.11V30.32C29.0401 31.2506 29.7945 32.005 30.7251 32.005Z"
            fill="white"
          />
        </svg>
      </LinkImage>
    )
    if (isPlst(CONSTANTS.BRAND as BRAND_LIST)) {
      return [PLIcon]
    }
    if (isGu(CONSTANTS.BRAND as BRAND_LIST)) {
      return [GUIcon]
    }
    if (
      isUniqlo(CONSTANTS.BRAND as BRAND_LIST) &&
      CONSTANTS.COUNTRY === Countries.KR
    ) {
      return [UQKRIcon]
    }
    return [UQIcon]
  }

  return (
    <div>
      {!isSimpleMode && !isNativeAppByUserAgent() && (
        <>
          <NavigationHeader
            contentArea={renderHeaderContent()}
            linkImageArea={renderIcons()}
            sticky
            ecRenewal
          />
        </>
      )}
      {isLanguageMenuOpen && windowWidth < 600 && (
        <OverlayBackdrop
          className="overlay-dropdown"
          onClick={() => {
            setLanguageMenuOpen(false)
          }}
        />
      )}
      <LayoutWrapper element="div" style={{ position: 'relative' }}>
        {children}
      </LayoutWrapper>

      {!isSimpleMode && !isNativeAppByUserAgent() && (
        <Footer
          copyrightRow={
            <Caption
              color="secondary"
              element="p"
              textAlign="left"
              textTransform="normal"
              variant="standard"
            >
              <FormattedMessage id="text_copyright" />
            </Caption>
          }
          footerLinksContainer={
            CONSTANTS.COUNTRY === Countries.AU && <FooterLinks intl={intl} />
          }
          footerLegalContainer={
            hasOneTrustFooter(CONSTANTS.COUNTRY, CONSTANTS.BRAND) && (
              <OneTrustFooter oneTrustLabel={oneTrustLabel} />
            )
          }
        />
      )}
    </div>
  )
}

export default withRouter(injectIntl(AppLayout))
